import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { TrendingUp, ArrowRight, Info, AlertTriangle } from "lucide-react";

function ScoreBar({ score }) {
  const color = score >= 70 ? "bg-emerald-500" : score >= 40 ? "bg-[#0047FF]" : "bg-slate-400";
  return (
    <div className="flex items-center gap-2">
      <div className="w-24 h-1.5 bg-[#E2E8F0] rounded-full overflow-hidden">
        <div className={color} style={{ width: `${score}%`, height: '100%' }} />
      </div>
      <span className="tabular-nums text-[#0A0D14] font-medium w-8 text-right">{score}</span>
    </div>
  );
}

export default function OpportunityPage() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(() => localStorage.getItem("sn_project") || "");
  const [items, setItems] = useState([]);
  const [minScore, setMinScore] = useState(0);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    api.get("/projects").then((r) => {
      setProjects(r.data);
      // Validate saved project id — see Overview.js for the same pattern.
      const saved = localStorage.getItem("sn_project") || "";
      const inList = saved && r.data.some((p) => p.id === saved);
      if (inList) {
        if (projectId !== saved) setProjectId(saved);
      } else if (r.data.length) {
        setProjectId(r.data[0].id);
        localStorage.setItem("sn_project", r.data[0].id);
      }
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!projectId) return;
    setLoadError(null);
    api.get("/opportunity", { params: { project_id: projectId, min_score: minScore } })
      .then((r) => { setItems(r.data); })
      .catch((err) => {
        // Surface the failure so agencies don't stare at a phantom-empty
        // table and assume they have no opportunities.
        const msg = err?.response?.data?.detail
          || (err?.response?.status ? `Opportunity data unavailable (HTTP ${err.response.status}).` : "Couldn't load opportunities. Check your connection and try again.");
        setLoadError(msg);
        setItems([]);
      });
  }, [projectId, minScore]);

  if (projects.length === 0) return <div className="p-10 text-[#8A94A6]">Create a project first. <Link to="/app/projects" className="text-[#0047FF]">Go →</Link></div>;

  return (
    <div className="p-8 lg:p-10">
      <header className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs text-[#8A94A6] uppercase tracking-wider font-medium">Opportunity ranking</div>
          <h1 className="mt-2 text-3xl font-display font-bold text-[#0A0D14]">Work on these first.</h1>
          <p className="mt-2 text-sm text-[#525B6A] max-w-xl">Score combines search volume, position potential, and recent trend. Higher score = more traffic gain per unit of SEO effort.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={projectId} onChange={(e) => { setProjectId(e.target.value); localStorage.setItem("sn_project", e.target.value); }} className="px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </header>

      <div className="mt-6 card-crisp p-4 flex items-center gap-4">
        <div className="text-xs font-medium text-[#525B6A]">Min score</div>
        <input type="range" min="0" max="100" value={minScore} onChange={(e) => setMinScore(parseInt(e.target.value))} className="flex-1" data-testid="min-score" />
        <div className="tabular-nums w-10 text-right text-[#0A0D14] font-medium">{minScore}</div>
      </div>

      {loadError && (
        <div className="mt-6 card-crisp p-4 flex items-start gap-3 border-amber-200 bg-amber-50" data-testid="opp-load-error">
          <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900">
            <div className="font-medium">Couldn&apos;t load opportunities.</div>
            <div className="mt-0.5 text-amber-800">{loadError}</div>
          </div>
        </div>
      )}

      <div className="mt-6 card-crisp overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FA] border-b border-[#E2E8F0] text-left text-[10px] uppercase tracking-wider text-[#8A94A6]">
              <tr>
                <th className="px-4 py-3 w-8">#</th>
                <th className="px-3 py-3">Keyword</th>
                <th className="px-3 py-3">Rank</th>
                <th className="px-3 py-3">Volume</th>
                <th className="px-3 py-3">Score</th>
                <th className="px-3 py-3">Why</th>
              </tr>
            </thead>
            <tbody>
              {items.map((k, i) => (
                <tr key={k.id} data-testid={`opp-row-${k.id}`} className="border-b border-[#E2E8F0] hover:bg-[#F8F9FA]">
                  <td className="px-4 py-3 text-[#8A94A6] tabular-nums">{i + 1}</td>
                  <td className="px-3 py-3">
                    <div className="text-[#0A0D14] font-medium">{k.keyword}</div>
                    <div className="text-xs text-[#8A94A6]">{k.country} · {k.device} · {k.engine}</div>
                  </td>
                  <td className="px-3 py-3 tabular-nums text-[#0A0D14] font-medium">{k.current_rank ? `#${k.current_rank}` : "—"}</td>
                  <td className="px-3 py-3 tabular-nums text-[#525B6A]">{k.search_volume ? k.search_volume.toLocaleString() : "—"}</td>
                  <td className="px-3 py-3"><ScoreBar score={k.opportunity_score} /></td>
                  <td className="px-3 py-3 text-xs text-[#525B6A] max-w-xs">{k.opportunity_reason}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan="6" className="text-center py-16 text-[#8A94A6]">
                  <TrendingUp className="h-6 w-6 mx-auto mb-2 text-[#E2E8F0]" />
                  No keywords at this score threshold.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 card-crisp p-4 flex items-start gap-2 text-xs text-[#525B6A]">
        <Info className="h-3.5 w-3.5 text-[#0047FF] mt-0.5 shrink-0" />
        <div>
          Score is 0-100. Volume weight caps at 40 (log-scale). Position potential peaks between #4-#20 (40 points). Recent movement adds 0-20.
          <Link to="/app/projects" className="text-[#0047FF] ml-2 inline-flex items-center gap-1">Open project <ArrowRight className="h-3 w-3" /></Link>
        </div>
      </div>
    </div>
  );
}
